import { useCallback, useEffect, useMemo, useState } from 'react';
import { addDays, endOfDay, format, startOfDay } from 'date-fns';
import { CalendarBooking, getCalendarBookings } from '../lib/bookings';
import { getCalendarFieldQuotes } from '../lib/fieldQuotes/calendarFieldQuotes';
import { getResources, Resource } from '../lib/resources';
import { getCalendarTasks } from '../lib/tasks';
import { supabase } from '../lib/supabase';
import { useBusiness } from '../contexts/BusinessContext';
import { usePermissionCheck } from './usePermissionCheck';

async function fetchTimeOffForDay(
  businessId: string,
  startDate: Date,
  endDate: Date
): Promise<CalendarBooking[]> {
  if (!supabase) return [];

  const { data, error } = await supabase
    .from('time_off_requests')
    .select(
      `
        id,
        start_date,
        end_date,
        request_type,
        is_partial_day,
        start_time,
        end_time,
        resource_id,
        resources:resource_id ( id, first_name, last_name )
      `
    )
    .eq('business_id', businessId)
    .eq('status', 'approved')
    .lte('start_date', format(endDate, 'yyyy-MM-dd'))
    .gte('end_date', format(startDate, 'yyyy-MM-dd'));

  if (error) {
    console.error('Error fetching time off:', error);
    return [];
  }

  const typeLabels: Record<string, string> = {
    vacation: 'Vacation',
    sick: 'Sick Leave',
    personal: 'Personal Day',
    family: 'Family Emergency',
    bereavement: 'Bereavement',
    other: 'Other',
  };

  return (data || []).map((request) => {
    const resource = Array.isArray(request.resources)
      ? request.resources[0]
      : request.resources;
    const resourceName = resource
      ? `${resource.first_name} ${resource.last_name}`
      : 'Unknown Resource';
    const typeLabel =
      typeLabels[request.request_type as keyof typeof typeLabels] || request.request_type;

    let endDateStr = request.end_date;
    if (!request.is_partial_day) {
      const [year, month, day] = request.end_date.split('-').map(Number);
      endDateStr = format(addDays(new Date(year, month - 1, day), 1), 'yyyy-MM-dd');
    }

    return {
      id: `timeoff_${request.id}`,
      title: `${typeLabel} - ${resourceName}`,
      booking_number: 0,
      start:
        request.is_partial_day && request.start_time
          ? `${request.start_date}T${request.start_time}:00`
          : request.start_date,
      end:
        request.is_partial_day && request.end_time
          ? `${request.end_date}T${request.end_time}:00`
          : endDateStr,
      backgroundColor: '#000000',
      borderColor: '#000000',
      allDay: !request.is_partial_day,
      display: 'block',
      resourceId: request.resource_id,
      extendedProps: {
        customer: resourceName,
        status: 'Time Off',
        booking_number: 0,
        subtotal: 0,
        total_amount: 0,
        is_time_off: true,
      },
    } as CalendarBooking;
  });
}

async function fetchBufferBlocksForDay(
  businessId: string,
  startDate: Date,
  endDate: Date,
  filterResourceId?: string
): Promise<CalendarBooking[]> {
  if (!supabase) return [];

  const fmtStart = format(startDate, 'yyyy-MM-dd');
  const fmtEnd = format(endDate, 'yyyy-MM-dd');

  let bufferQuery = supabase
    .from('mobile_bookings')
    .select(
      'id, resource_id, asset_id, booking_id, start_date, start_time, end_date, end_time, buffer_time'
    )
    .eq('business_id', businessId)
    .eq('is_buffer_block', true)
    .lte('start_date', fmtEnd)
    .gte('end_date', fmtStart);

  if (filterResourceId) {
    bufferQuery = bufferQuery.or(
      `resource_id.eq.${filterResourceId},asset_id.eq.${filterResourceId}`
    );
  }

  const { data, error } = await bufferQuery;
  if (error || !data) return [];

  return data.map((block) => {
    const [sy, sm, sd] = block.start_date.split('-').map(Number);
    const [sh, smin] = block.start_time.split(':').map(Number);
    const startDateObj = new Date(sy, sm - 1, sd, sh, smin, 0, 0);
    const [ey, em, ed] = block.end_date.split('-').map(Number);
    const [eh, emin] = block.end_time.split(':').map(Number);
    const endDateObj = new Date(ey, em - 1, ed, eh, emin, 0, 0);
    const blockOwnerId = block.resource_id || block.asset_id;

    return {
      id: `buffer-${block.id}`,
      title: 'Buffer',
      booking_number: 0,
      start: startDateObj.toISOString(),
      end: endDateObj.toISOString(),
      backgroundColor: '#000000',
      borderColor: '#000000',
      allDay: false,
      display: 'block',
      resourceId: blockOwnerId,
      extendedProps: {
        customer: '',
        booking_number: 0,
        subtotal: 0,
        total_amount: 0,
        status: 'buffer',
        is_buffer_block: true,
        booking_id: block.booking_id,
        resource_id: blockOwnerId,
      },
    } as CalendarBooking;
  });
}

export function useCalendarDayScheduleData(selectedDate: Date = new Date()) {
  const { selectedBusiness } = useBusiness();
  const { canAccess, currentResource } = usePermissionCheck();

  const [bookings, setBookings] = useState<CalendarBooking[]>([]);
  const [timeOffRequests, setTimeOffRequests] = useState<CalendarBooking[]>([]);
  const [fieldQuoteEvents, setFieldQuoteEvents] = useState<CalendarBooking[]>([]);
  const [resources, setResources] = useState<Resource[]>([]);
  const [mapViewEnabled, setMapViewEnabled] = useState(false);
  const [salesPinsEnabled, setSalesPinsEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const dayRange = useMemo(() => {
    const start = startOfDay(selectedDate);
    const end = endOfDay(selectedDate);
    return { start, end };
  }, [selectedDate]);

  const filterResourceId = useMemo(() => {
    if (canAccess.viewAllBookings || !currentResource?.id) return undefined;
    return currentResource.id;
  }, [canAccess.viewAllBookings, currentResource?.id]);

  const load = useCallback(async () => {
    if (!selectedBusiness?.id || !supabase) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { start, end } = dayRange;

      const [
        mapViewData,
        salesPinsData,
        tasksData,
        locationBookings,
        mobileBookings,
        timeOff,
        fieldQuotes,
        bufferBlocks,
        resourceList,
      ] = await Promise.all([
        supabase.rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: 'calendar',
          p_setting_key: 'map_view_enabled',
          p_default_value: false,
        }),
        supabase.rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: 'calendar',
          p_setting_key: 'sales_pins_enabled',
          p_default_value: true,
        }),
        supabase.rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: 'calendar',
          p_setting_key: 'show_tasks_in_fab',
          p_default_value: false,
        }),
        getCalendarBookings(selectedBusiness.id, start, end, true, filterResourceId),
        getCalendarBookings(selectedBusiness.id, start, end, false, filterResourceId),
        fetchTimeOffForDay(selectedBusiness.id, start, end),
        getCalendarFieldQuotes(selectedBusiness.id, start, end, filterResourceId),
        fetchBufferBlocksForDay(selectedBusiness.id, start, end, filterResourceId),
        getResources(selectedBusiness.id),
      ]);

      if (!mapViewData.error && mapViewData.data !== null) {
        setMapViewEnabled(!!mapViewData.data);
      }
      if (!salesPinsData.error && salesPinsData.data !== null) {
        setSalesPinsEnabled(!!salesPinsData.data);
      }

      const tasksEnabled = !tasksData.error && tasksData.data === true;
      const tasks = tasksEnabled
        ? await getCalendarTasks(selectedBusiness.id, start, end, filterResourceId)
        : [];

      setBookings([
        ...locationBookings,
        ...mobileBookings,
        ...bufferBlocks,
        ...tasks,
      ]);
      setTimeOffRequests(timeOff);
      setFieldQuoteEvents(fieldQuotes);
      setResources(
        (resourceList || []).sort((a, b) =>
          `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`)
        )
      );
    } catch (err) {
      console.error('Error loading day schedule:', err);
      setError(err instanceof Error ? err.message : 'Failed to load schedule');
    } finally {
      setLoading(false);
    }
  }, [selectedBusiness?.id, dayRange, filterResourceId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const refresh = () => void load();
    const events = [
      'bookingCreated',
      'mobileBookingCreated',
      'taskCreated',
      'controlCentreCalendarRefresh',
    ] as const;
    events.forEach((name) => window.addEventListener(name, refresh));
    return () => events.forEach((name) => window.removeEventListener(name, refresh));
  }, [load]);

  return {
    bookings,
    timeOffRequests,
    fieldQuoteEvents,
    resources,
    mapViewEnabled,
    salesPinsEnabled,
    loading,
    error,
    refresh: load,
    businessHours: selectedBusiness?.business_hours,
    businessId: selectedBusiness?.id,
  };
}
